import React, { memo, useState, ChangeEvent, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Select, ISelectOption } from 'components/forms/Selects'
import { Button } from 'transfix-ui/components/Button'
import { Card } from 'transfix-ui/components/Card'
import { Checkbox } from 'transfix-ui/components/Checkbox'
import { IconButton } from 'transfix-ui/components/IconButton'
import { updateSpotBiddingConfig } from 'apps/spot-quote-control-panel/utils/actions/spotBiddingConfig'
import { TextInput } from 'components/atoms'
import { EditIcon } from 'transfix-icons'
import { capitalize } from 'utils/capitalize'
import { useFeatureFlagContext } from '../../../utils/context/FeatureFlagsContext'
import { QUOTE_TYPE_OPTIONS, MODIFY_PP_TYPE_OPTIONS } from '../constants/spotBiddingConfig'
import {
  RATE_TYPE,
  ISpotBiddingConfig,
  CONFIG_TYPE,
  MODIFY_PAST_PICKUP,
} from '../types/spotBiddingConfig'

interface IShipperQuoteTypeProps {
  spotBiddingConfig: ISpotBiddingConfig
}

export const ShipperQuoteType = memo<IShipperQuoteTypeProps>(({ spotBiddingConfig }) => {
  const dispatch = useDispatch()
  const { getFlag } = useFeatureFlagContext()
  const [editing, setEditing] = useState(false)
  const [configState, setConfigState] = useState({ ...spotBiddingConfig })
  const [showDropDown, dropDown] = useState(false)
  const { enabled: ppDateAdjustIsEnabled } = getFlag('faPastPickupDateAdjustment')

  const {
    fuelRpm: fuelRpmState,
    modifyPastPickupType: modifyPpState,
    rateType: rateTypeState,
  } = configState

  useEffect(() => {
    setConfigState({ ...spotBiddingConfig })
  }, [editing])

  const toggleEdit = () => setEditing(!editing)
  const quoteTypeHandler = (e: ISelectOption) => {
    setConfigState({
      ...configState,
      rateType: e.value as RATE_TYPE,
      fuelRpm: e.value === RATE_TYPE.linehaul ? fuelRpmState : '',
    })
  }
  const selected = MODIFY_PP_TYPE_OPTIONS.filter(option => option.value === modifyPpState)[0] || ''
  const modifyPastDateHandler = (e: ISelectOption) => {
    setConfigState({
      ...configState,
      modifyPastPickupType: e.value as MODIFY_PAST_PICKUP,
    })
  }
  const fuelRpmHandler = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setConfigState({
      ...configState,
      fuelRpm: value,
    })
  }
  const updateShipperConfig = () => {
    dispatch(updateSpotBiddingConfig(configState))
  }
  const quoteType = capitalize(rateTypeState?.replace('_', '-'))
  const onModifyPickupDateChange = () => {
    dropDown(!showDropDown)
  }

  return (
    <Card>
      <div className='row mb3'>
        <h3 className='fl heading-medium'>Details</h3>
        <div className='fr'>
          <IconButton
            Icon={EditIcon}
            data-test='edit-shipper-config'
            label='Edit Shipper Config'
            onClick={() => {
              toggleEdit()
              dropDown(false)
            }}
            disabled={spotBiddingConfig?.configType === CONFIG_TYPE.global}
            size='large'
          />
        </div>
      </div>
      <hr />
      <div>
        {!editing && (
          <>
            <div className='row'>
              <div className='col-md-6'>
                <b>Quote Type: </b>
                {quoteType}
                {fuelRpmState && (
                  <>
                    <b> Fuel (rate per mile): </b>
                    {fuelRpmState}
                  </>
                )}
              </div>
            </div>
            {ppDateAdjustIsEnabled && (
              <div className='row'>
                <div className='col-md-6'>
                  <b>Modify Past Pickup Dates?</b>
                </div>
                {modifyPpState !== null ? (
                  <div className='col-md-6'>{selected.label}</div>
                ) : (
                  <div className='col-md-6'>Not set</div>
                )}
              </div>
            )}
          </>
        )}
        {editing && (
          <>
            <div className='row'>
              <div className='col-md-4 col-lg-4'>
                <Select
                  label='Quote Type'
                  value={rateTypeState}
                  options={QUOTE_TYPE_OPTIONS}
                  onChange={quoteTypeHandler}
                />
              </div>
              {rateTypeState === RATE_TYPE.linehaul && (
                <div className='col-md-4 col-lg-4'>
                  <TextInput
                    label='Fuel (RPM)'
                    value={fuelRpmState}
                    onChange={fuelRpmHandler}
                    type='number'
                  />
                </div>
              )}
            </div>
            {ppDateAdjustIsEnabled && (
              <>
                <div className='row'>
                  <Checkbox label='Modify Past Pickup Date?' onChange={onModifyPickupDateChange} />
                </div>
                {showDropDown && (
                  <div className='row'>
                    <Select
                      options={MODIFY_PP_TYPE_OPTIONS}
                      value={selected}
                      onChange={modifyPastDateHandler}
                      id='modify-past-pickup'
                      dataTest='modify-past-pickup'
                    />
                  </div>
                )}
              </>
            )}
            <div className='mt4 pt1'>
              <Button
                color='tertiary'
                onClick={() => {
                  toggleEdit()
                  dropDown(false)
                }}
                data-test='bid-component-button'>
                Cancel
              </Button>
              <Button
                color='primary'
                onClick={updateShipperConfig}
                data-test='bid-component-button'>
                Save
              </Button>
            </div>
          </>
        )}
      </div>
    </Card>
  )
})
